/**
 * da-cart-cro.js — CRO triggers for the Pitch cart drawer.
 *
 * Vanilla JS, no libraries (Oliver Kenyon principle). Two custom elements:
 *   <da-reserve-timer>  — 15-min (configurable) countdown, persisted in
 *                         sessionStorage so it survives drawer re-renders and
 *                         does not reset on every cart update.
 *   <da-cart-upsell>    — one-click add of a suggested product via
 *                         fetch('/cart/add.js') + Shopify.actions.updateCart().
 *
 * Both are self-contained custom elements: they run on connect, so they work
 * automatically when the cart-drawer-section is re-rendered by the Section
 * Rendering API. `shopify:section:load` is also handled for the theme editor.
 */
(function () {
  'use strict';

  var STORAGE_KEY = 'da-cart-reserve-deadline';

  /* ---------------------------------------------------------------- Timer -- */
  class ReserveTimerElement extends HTMLElement {
    connectedCallback() {
      this.valueEl = this.querySelector('.da-cro-timer__value');
      var minutes = parseInt(this.getAttribute('data-minutes'), 10);
      this.durationMs = (isNaN(minutes) ? 15 : minutes) * 60 * 1000;
      this.start();
    }

    disconnectedCallback() {
      this.stop();
    }

    getDeadline() {
      var stored = null;
      try {
        stored = sessionStorage.getItem(STORAGE_KEY);
      } catch (e) {
        stored = null;
      }
      var now = Date.now();
      var deadline = stored ? parseInt(stored, 10) : NaN;

      // (Re)start when there is no valid, future deadline.
      if (isNaN(deadline) || deadline <= now) {
        deadline = now + this.durationMs;
        try {
          sessionStorage.setItem(STORAGE_KEY, String(deadline));
        } catch (e) {
          /* storage unavailable — timer still works for this render */
        }
      }
      return deadline;
    }

    start() {
      this.stop();
      this.deadline = this.getDeadline();
      this.tick();
      this.interval = window.setInterval(this.tick.bind(this), 1000);
    }

    stop() {
      if (this.interval) {
        window.clearInterval(this.interval);
        this.interval = null;
      }
    }

    tick() {
      if (!this.valueEl) return;
      var remaining = this.deadline - Date.now();
      if (remaining <= 0) {
        remaining = 0;
        this.stop();
      }
      var totalSec = Math.floor(remaining / 1000);
      var mm = Math.floor(totalSec / 60);
      var ss = totalSec % 60;
      this.valueEl.textContent = pad(mm) + ':' + pad(ss);
    }
  }

  function pad(n) {
    return n < 10 ? '0' + n : String(n);
  }

  /* --------------------------------------------------------------- Upsell -- */
  class CartUpsellElement extends HTMLElement {
    connectedCallback() {
      this.button = this.querySelector('[data-da-upsell-add]');
      if (!this.button) return;
      this.onClick = this.handleClick.bind(this);
      this.button.addEventListener('click', this.onClick);
    }

    disconnectedCallback() {
      if (this.button && this.onClick) {
        this.button.removeEventListener('click', this.onClick);
      }
    }

    handleClick() {
      var variantId = this.getAttribute('data-variant-id');
      if (!variantId || this.busy) return;
      this.busy = true;
      if (this.button) this.button.setAttribute('aria-busy', 'true');

      // Ask for the cart-drawer section back so the theme can morph it in.
      var body = {
        items: [{ id: Number(variantId), quantity: 1 }],
        sections: ['cart-drawer-section'],
      };

      var routeAdd =
        (window.Theme && window.Theme.routes && window.Theme.routes.cart_add_url) || '/cart/add.js';

      fetch(routeAdd, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
        body: JSON.stringify(body),
      })
        .then(function (res) {
          return res.json();
        })
        .then(
          function () {
            // Let the native cart machinery re-render the drawer + header count.
            var actions = window.Shopify && window.Shopify.actions;
            if (actions && actions.updateCart && typeof actions.updateCart === 'function') {
              try {
                actions.updateCart();
                return;
              } catch (e) {
                /* fall through to manual refresh */
              }
            }
            // Fallback: re-fetch the cart-drawer section and swap its inner content.
            this.refreshDrawerFallback();
          }.bind(this)
        )
        .catch(
          function () {
            this.busy = false;
            if (this.button) this.button.removeAttribute('aria-busy');
          }.bind(this)
        );
    }

    refreshDrawerFallback() {
      fetch('/?section_id=cart-drawer-section')
        .then(function (res) {
          return res.text();
        })
        .then(function (html) {
          var parser = new DOMParser();
          var doc = parser.parseFromString(html, 'text/html');
          var next = doc.querySelector('.cart-drawer__inner');
          var current = document.querySelector('#cart-drawer .cart-drawer__inner');
          if (next && current && current.parentNode) {
            current.parentNode.replaceChild(next, current);
          }
        })
        .catch(function () {})
        .finally(
          function () {
            this.busy = false;
            if (this.button) this.button.removeAttribute('aria-busy');
          }.bind(this)
        );
    }
  }

  /* ------------------------------------------------------------------ Gift -- */
  // Auto-adds/removes the free-gift variant when the cart crosses the gift
  // threshold. The gift's price is zeroed by a Shopify automatic discount
  // (100% off, min subtotal), so adding/removing it never changes the
  // subtotal and cannot cause the unlocked state to flap.
  var GIFT_INFLIGHT_KEY = '__daCartGiftInFlight';

  class CartGiftElement extends HTMLElement {
    connectedCallback() {
      this.sync();
    }

    sync() {
      var variantId = parseInt(this.getAttribute('data-variant-id'), 10);
      var unlocked = this.getAttribute('data-unlocked') === 'true';
      if (!variantId || window[GIFT_INFLIGHT_KEY]) return;

      window[GIFT_INFLIGHT_KEY] = true;
      fetch('/cart.js')
        .then(function (res) {
          return res.json();
        })
        .then(
          function (cart) {
            var line = null;
            var lineIndex = -1;
            for (var i = 0; i < cart.items.length; i++) {
              if (cart.items[i].variant_id === variantId) {
                line = cart.items[i];
                lineIndex = i + 1; // /cart/change.js line is 1-based
                break;
              }
            }

            if (unlocked && !line) {
              return this.addGift(variantId);
            }
            if (!unlocked && line) {
              return this.removeGift(lineIndex);
            }
            window[GIFT_INFLIGHT_KEY] = false;
          }.bind(this)
        )
        .catch(function () {
          window[GIFT_INFLIGHT_KEY] = false;
        });
    }

    addGift(variantId) {
      return fetch('/cart/add.js', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
        body: JSON.stringify({ items: [{ id: variantId, quantity: 1 }] }),
      })
        .then(this.refreshCart.bind(this))
        .catch(function () {
          window[GIFT_INFLIGHT_KEY] = false;
        });
    }

    removeGift(lineIndex) {
      return fetch('/cart/change.js', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
        body: JSON.stringify({ line: lineIndex, quantity: 0 }),
      })
        .then(this.refreshCart.bind(this))
        .catch(function () {
          window[GIFT_INFLIGHT_KEY] = false;
        });
    }

    refreshCart() {
      window[GIFT_INFLIGHT_KEY] = false;
      var actions = window.Shopify && window.Shopify.actions;
      if (actions && actions.updateCart && typeof actions.updateCart === 'function') {
        try {
          actions.updateCart();
        } catch (e) {
          /* section will still be correct on next natural re-render */
        }
      }
    }
  }

  /* --------------------------------------------------------------- Define -- */
  function define() {
    if (!customElements.get('da-reserve-timer')) {
      customElements.define('da-reserve-timer', ReserveTimerElement);
    }
    if (!customElements.get('da-cart-upsell')) {
      customElements.define('da-cart-upsell', CartUpsellElement);
    }
    if (!customElements.get('da-cart-gift')) {
      customElements.define('da-cart-gift', CartGiftElement);
    }
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', define, { once: true });
  } else {
    define();
  }

  // Theme editor: custom elements auto-reconnect, but ensure defs exist.
  document.addEventListener('shopify:section:load', define);
})();
